import { Badge, Button, Card, Typography } from '@mui/material';
import { startCase } from 'lodash';
import { useRouter } from 'next/router';
import { useEffectOnce } from 'react-use';
import { formatAddress } from '@/common/utils/address';
import {
  TrackHomePageEvent,
  useTrackHomePageAnalytics,
} from '$analytics/useTrackAnalytics/domains/homePage';
import type { SideListing } from '../../types';
import { isRecentDate } from './helpers';
import { PropertyInfo } from './PropertyInfo';

interface Props {
  listing: SideListing;
  index: number;
}

export const ListingCard = ({ listing, index }: Props) => {
  const { push } = useRouter();
  const trackEvent = useTrackHomePageAnalytics();

  const { address, status, id } = listing;

  useEffectOnce(() => {
    trackEvent(TrackHomePageEvent.SideListingsCardMount, {
      interactionId: id,
    });
  });

  const formattedStatus = startCase(status);

  const handleCardClick = () => {
    trackEvent(TrackHomePageEvent.SideListingsCardClick, { interactionId: id });
    void push(
      `/community/listings?listingId=${id}&utm_source=hp&utm_medium=slc`,
    );
  };

  return (
    <Card
      name={`side-listings-card-${index}`}
      onClick={handleCardClick}
      component={Button}
      sx={{
        textAlign: 'unset',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        borderRadius: '4px',
        px: (theme) => theme.spacing(2),
        py: (theme) => theme.spacing(1),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
        }}
      >
        {isRecentDate(listing.dateAdded) && (
          <Badge variant="dot" color="secondary" sx={{ mr: 1 }} />
        )}
        {formattedStatus}
      </Typography>
      {address && (
        <Typography variant="body2" sx={{ pb: 1 }}>
          {formatAddress(address, { includeStreet2: true })}
        </Typography>
      )}
      <PropertyInfo {...listing} />
    </Card>
  );
};
